import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  RichText
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Notice,
  Button,
  TextControl
} from "@wordpress/components"
import { useState } from "@wordpress/element"

export default function Edit({ attributes, setAttributes }) {
  const blockProps = useBlockProps({
    className: "services-overview"
  })

  const [isEditing, setIsEditing] = useState(false)

  const {
    service1Title,
    service1Description,
    service1Url,
    service2Title,
    service2Description,
    service2Url,
    service3Title,
    service3Description,
    service3Url,
    service4Title,
    service4Description,
    service4Url,
    service5Title,
    service5Description,
    service5Url
  } = attributes

  // Service data matching exactly render.php
  const services = [
    {
      title: service1Title || 'Water Damage',
      description: service1Description || 'Rapid response to mitigate water damage and prevent further issues.',
      url: service1Url || '',
      color: '#C50A1D',
      titleAttr: 'service1Title',
      descAttr: 'service1Description',
      urlAttr: 'service1Url'
    },
    {
      title: service2Title || 'Fire and Smoke Damage',
      description: service2Description || 'Comprehensive soot and smoke cleanup to restore your property.',
      url: service2Url || '',
      color: '#C50A1D',
      titleAttr: 'service2Title',
      descAttr: 'service2Description',
      urlAttr: 'service2Url'
    },
    {
      title: service3Title || 'Mold Removal',
      description: service3Description || 'Effective mold detection and remediation for a healthier environment.',
      url: service3Url || '',
      color: '#C50A1D',
      titleAttr: 'service3Title',
      descAttr: 'service3Description',
      urlAttr: 'service3Url'
    },
    {
      title: service4Title || 'Biohazard Cleanup',
      description: service4Description || 'Safe and thorough cleaning of hazardous materials and pathogens.',
      url: service4Url || '',
      color: '#00346D',
      titleAttr: 'service4Title',
      descAttr: 'service4Description',
      urlAttr: 'service4Url'
    },
    {
      title: service5Title || 'Reconstruction',
      description: service5Description || 'Expert repairs and rebuilding after mitigation to fully restore your property.',
      url: service5Url || '',
      color: '#C50A1D',
      titleAttr: 'service5Title',
      descAttr: 'service5Description',
      urlAttr: 'service5Url'
    }
  ]

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Services Overview Settings', 'puroclean')} initialOpen={true}>
          <Notice status="info" isDismissible={false}>
            <p>
              <strong>Services Content:</strong> {isEditing ? 'Click on any service title or description to edit' : 'Click "Edit Services" to modify content'}<br />
              <strong>Icons:</strong> Automatically configured (red for most services, blue for biohazard)<br />
              <strong>URLs:</strong> Add links to make services clickable with hover effects<br />
              <strong>Preview:</strong> Shows exactly how it appears on frontend
            </p>
          </Notice>
          
          <div style={{ marginTop: '15px' }}>
            <Button 
              variant={isEditing ? "primary" : "secondary"}
              onClick={() => setIsEditing(!isEditing)}
              style={{ width: '100%' }}
            >
              {isEditing ? __('Done Editing', 'puroclean') : __('Edit Services', 'puroclean')}
            </Button>
          </div>
        </PanelBody>

        <PanelBody title={__('Service URLs', 'puroclean')} initialOpen={true}>
          {services.map((service, index) => (
            <TextControl
              key={index}
              label={`${service.title} URL`}
              value={service.url}
              onChange={(value) => setAttributes({ [service.urlAttr]: value })}
              placeholder="https://example.com/service-page"
              help="Leave empty to disable linking for this service"
            />
          ))}
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="py-0 mt-0 bg-gray-50 pb-12">
          <div className="max-w-content mx-auto px-4">
            <div className="bg-white rounded-t-[0px] rounded-b-[10px] shadow-[0_0_20px_rgba(0,0,0,0.06)] p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                {services.map((service, index) => (
                  <div key={index} className="flex flex-col">
                    {service.url && service.url !== '#' ? (
                      <a href={service.url} className="flex items-start group transition-colors duration-200 hover:cursor-pointer" onClick={(e) => e.preventDefault()}>
                        {/* Service Icon */}
                        <div className="flex-shrink-0">
                          <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-black group-hover:text-secondary transition-colors duration-200">
                            <rect width="40" height="40"/>
                          </svg>
                        </div>
                        
                        {/* Service Content */}
                        <div className="ml-3">
                          {isEditing ? (
                            <>
                              <RichText
                                tagName="h3"
                                placeholder={__('Service title...', 'puroclean')}
                                value={service.title}
                                onChange={(value) => setAttributes({ [service.titleAttr]: value })}
                                className="text-lg font-semibold mb-2 leading-tight group-hover:text-secondary transition-colors duration-200"
                              />
                              <RichText
                                tagName="p"
                                placeholder={__('Service description...', 'puroclean')}
                                value={service.description}
                                onChange={(value) => setAttributes({ [service.descAttr]: value })}
                                className="text-base leading-relaxed text-gray-700"
                              />
                            </>
                          ) : (
                            <>
                              <h3 className="text-lg font-semibold mb-2 leading-tight group-hover:text-secondary transition-colors duration-200">
                                {service.title}
                              </h3>
                              <p className="text-base leading-relaxed text-gray-700">
                                {service.description}
                              </p>
                              <p className="text-xs text-green-600 mt-1">
                                🔗 Clickable (hover to see effect)
                              </p>
                            </>
                          )}
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-start">
                        {/* Service Icon */}
                        <div className="flex-shrink-0">
                          <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-black">
                            <rect width="40" height="40"/>
                          </svg>
                        </div>
                        
                        {/* Service Content */}
                        <div className="ml-3">
                          {isEditing ? (
                            <>
                              <RichText
                                tagName="h3"
                                placeholder={__('Service title...', 'puroclean')}
                                value={service.title}
                                onChange={(value) => setAttributes({ [service.titleAttr]: value })}
                                className="text-lg font-semibold mb-2 leading-tight"
                              />
                              <RichText
                                tagName="p"
                                placeholder={__('Service description...', 'puroclean')}
                                value={service.description}
                                onChange={(value) => setAttributes({ [service.descAttr]: value })}
                                className="text-base leading-relaxed text-gray-700"
                              />
                            </>
                          ) : (
                            <>
                              <h3 className="text-lg font-semibold mb-2 leading-tight">
                                {service.title}
                              </h3>
                              <p className="text-base leading-relaxed text-gray-700">
                                {service.description}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                No URL set
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Edit mode indicator */}
        {isEditing && (
          <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-xs">
            ✏️ Edit Mode
          </div>
        )}
      </div>
    </>
  )
} 